import React, { useEffect, useRef, useState } from 'react';
import api, { isLimitError } from '../api/api';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import hooks from '../data/hooks.json';
import { parseAiJson, parseAiList } from '../utils/parseAiJson';
import { storage } from '../product/productStorage';
import { useCreateContext } from '../product/CreateContext';
import { CopyButton } from './create/generatorUi';
import { useCreateFlow, useFlowStep } from './create/flow';
import './hook.css';

const HOOK_COUNT = 5;
const TEMPLATE_EXAMPLES = 3;

const EMOTIONAL_APPEALS = [
    ['curiosity', 'Curiosity'],
    ['fomo', 'FOMO'],
    ['solution', 'Solution-focused'],
    ['aspiration', 'Aspirational'],
    ['fear', 'Fear of missing out'],
    ['social_proof', 'Social proof'],
];
const FORMATS = [
    ['statement', 'Statement'],
    ['question', 'Question'],
    ['statistic', 'Statistic'],
    ['story', 'Story'],
    ['problem_solution', 'Problem → solution'],
    ['shocking_fact', 'Shocking fact'],
];
const CTA_TYPES = [
    ['learn_more', 'Learn more'],
    ['buy_now', 'Buy now'],
    ['sign_up', 'Sign up'],
    ['discover', 'Discover'],
    ['try_free', 'Try free'],
    ['contact', 'Contact us'],
];
const LENGTHS = [['5', '~5 words'], ['10', '~10 words'], ['15', '~15 words'], ['20', '~20 words']];
const AUDIENCES = [
    ['b2b', 'Business (B2B)'],
    ['b2c', 'Consumer (B2C)'],
    ['youth', 'Youth'],
    ['senior', 'Senior'],
    ['luxury', 'Luxury'],
    ['budget', 'Budget-conscious'],
];
const INDUSTRIES = [
    ['general', 'General'],
    ['tech', 'Technology'],
    ['health', 'Health & wellness'],
    ['finance', 'Finance'],
    ['education', 'Education'],
    ['retail', 'Retail'],
    ['food', 'Food & beverage'],
    ['travel', 'Travel'],
];
const TONES = [
    ['professional', 'Professional'],
    ['casual', 'Casual'],
    ['humorous', 'Humorous'],
    ['dramatic', 'Dramatic'],
    ['urgent', 'Urgent'],
    ['friendly', 'Friendly'],
    ['authoritative', 'Authoritative'],
];

const pickRandom = (items, count) => {
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
};

const describeTemplate = (template) => {
    let line = `- "${template.hook}" (e.g. "${template.example}")`;
    if (template.notes) line += ` Note: ${template.notes}`;
    return line;
};

const hookText = (item) => {
    const value = item && typeof item === 'object' ? item.hook ?? item.text : item;
    return typeof value === 'string' ? value.trim() : '';
};

// Hooks from an AI reply: a JSON array, {"hooks": [...]}, or a plain list.
// Anything else gives an empty list instead of throwing.
const parseHooks = (response) => {
    const parsed = parseAiJson(response);
    let items = [];
    if (Array.isArray(parsed)) {
        items = parsed;
    } else if (parsed && typeof parsed === 'object') {
        items = Array.isArray(parsed.hooks) ? parsed.hooks : Object.values(parsed).find(Array.isArray) || [];
    } else if (typeof response === 'string') {
        try {
            // A single line of plain text is usually a refusal or a remark, not a list of hooks
            const lines = parseAiList(response);
            items = lines.length > 1 ? lines : [];
        } catch (e) {
            items = [];
        }
    }
    // Hooks are selected by their text, so drop duplicates
    return [...new Set(items.map(hookText).filter(Boolean))];
};

const Select = ({ label, value, onChange, options, className = '' }) => (
    <label className={`ls-field ${className}`}>
        <span className="ls-label">{label}</span>
        <select className="ls-select" value={value} onChange={(e) => onChange(e.target.value)}>
            {options.map(([id, text]) => <option key={id} value={id}>{text}</option>)}
        </select>
    </label>
);

const HookScreen = () => {
    const { product, icp, promptContext, seed, embedded } = useCreateContext();
    const { flow, report, update } = useCreateFlow();
    const { next, goNext } = useFlowStep();
    const [productName, setProductName] = useState(product?.name || '');
    const [productDescription, setProductDescription] = useState(seed?.topic || product?.oneLiner || '');
    const [generatedHooks, setGeneratedHooks] = useState([]);
    const [selectedHooks, setSelectedHooks] = useState([]);
    const [templateIndex, setTemplateIndex] = useState('any');
    const [isLoading, setIsLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [error, setError] = useState('');
    const [tone, setTone] = useState('professional');
    const [maxLength, setMaxLength] = useState('10');
    const [targetAudience, setTargetAudience] = useState('general');
    const [industry, setIndustry] = useState('general');
    const [ctaType, setCtaType] = useState('learn_more');
    const [hookFormat, setHookFormat] = useState('statement');
    const [emotionalAppeal, setEmotionalAppeal] = useState('curiosity');
    const descriptionRef = useRef(null);

    // What the post is about carries on to the Script and Caption steps
    useEffect(() => {
        update({ topic: productDescription.trim() });
    }, [productDescription, update]);

    const handleGenerateHook = async () => {
        if (!productDescription.trim()) {
            setFormError('Describe what the hook will promote, then try again.');
            descriptionRef.current?.focus();
            return;
        }
        const templates = templateIndex === 'any'
            ? pickRandom(hooks, TEMPLATE_EXAMPLES)
            : [hooks[Number(templateIndex)]];
        const templateInstruction = templateIndex === 'any'
            ? "Use these proven hook templates as style references (you don't have to copy them exactly):"
            : 'Every hook must follow this template:';

        const prompt = `You are a TikTok expert who writes viral short-form video hooks that stop people from scrolling.
Write ${HOOK_COUNT} different hooks for the following product.

Product name: ${productName || '(not specified)'}
Product description: ${productDescription}
${promptContext ? `\nWhat you know about the product, who it's for and the brand voice (write for this person, in this voice):\n${promptContext}\n` : ''}Target audience: ${targetAudience === 'general' && icp ? icp.name : targetAudience}
Industry: ${industry}
Tone: ${tone}
Format: ${hookFormat}
Emotional appeal: ${emotionalAppeal}
Call to action style: ${ctaType}
Each hook should be about ${maxLength} words.

${templateInstruction}
${templates.map(describeTemplate).join('\n')}

Respond ONLY with a JSON array of ${HOOK_COUNT} strings, no other text.`;

        setFormError('');
        setError('');
        setIsLoading(true);
        try {
            const response = await api.getChatCompletion(prompt);
            const parsedHooks = parseHooks(response).slice(0, HOOK_COUNT);
            if (parsedHooks.length === 0) {
                throw new Error('No hooks in response');
            }
            setGeneratedHooks(parsedHooks);
            setSelectedHooks([]);
        } catch (e) {
            if (isLimitError(e)) return;
            setError("Couldn't write hooks this time. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const toggleHook = (hook) => {
        const next = selectedHooks.includes(hook)
            ? selectedHooks.filter(h => h !== hook)
            : [...selectedHooks, hook];
        setSelectedHooks(next);
        // The last one picked is the post's hook: the Script, Video and Caption steps start from it.
        // Unpicking them all goes back to the planned post's own hook, if it has one.
        const picked = next[next.length - 1] || (seed?.hook || '').trim();
        report('hook', { hook: picked, hooks: next }, Boolean(picked));
        storage.setJson('selectedHooks', next);
    };

    const fromPlan = Boolean(flow.hook) && selectedHooks.length === 0 && flow.hook === (seed?.hook || '').trim();

    const audienceOptions = [['general', icp ? `${icp.name} (ICP)` : 'General'], ...AUDIENCES];

    return (
        <>
            {!embedded && (
                <div className="ls-page-header">
                    <h1 className="ls-page-title">Hook generator</h1>
                </div>
            )}

            <div className="ls-two-col">
                <div className="ls-card">
                    <div>
                        <div className="ls-card-title">Hook brief</div>
                        <div className="ls-card-sub">The first line of your video: what makes people stop scrolling.</div>
                    </div>
                    <div className="ls-form ls-hook-form">
                        <div className="ls-section-title">Product</div>
                        <label className="ls-field">
                            <span className="ls-label">Product name</span>
                            <input
                                className="ls-input"
                                type="text"
                                value={productName}
                                onChange={(e) => setProductName(e.target.value)}
                                placeholder="e.g. Brewly"
                            />
                        </label>
                        <label className="ls-field">
                            <span className="ls-label">What are you promoting?</span>
                            <textarea
                                ref={descriptionRef}
                                className="ls-textarea"
                                rows={2}
                                value={productDescription}
                                aria-invalid={Boolean(formError)}
                                onChange={(e) => {
                                    setProductDescription(e.target.value);
                                    if (e.target.value.trim()) setFormError('');
                                }}
                                placeholder="e.g. Fresh coffee delivered to remote teams every week"
                            />
                        </label>

                        <div className="ls-section-title">Hook strategy</div>
                        <div className="ls-form-grid">
                            <Select
                                className="is-wide"
                                label="Template"
                                value={templateIndex}
                                onChange={setTemplateIndex}
                                options={[['any', 'Any (a mix of proven templates)'], ...hooks.map((template, index) => [String(index), template.hook])]}
                            />
                            <Select label="Emotional appeal" value={emotionalAppeal} onChange={setEmotionalAppeal} options={EMOTIONAL_APPEALS} />
                            <Select label="Call to action" value={ctaType} onChange={setCtaType} options={CTA_TYPES} />
                        </div>

                        <div className="ls-section-title">Audience</div>
                        <div className="ls-form-grid">
                            <Select label="Who it's for" value={targetAudience} onChange={setTargetAudience} options={audienceOptions} />
                            <Select label="Industry" value={industry} onChange={setIndustry} options={INDUSTRIES} />
                        </div>

                        <div className="ls-section-title">Style</div>
                        <div className="ls-form-grid is-3">
                            <Select label="Format" value={hookFormat} onChange={setHookFormat} options={FORMATS} />
                            <Select label="Tone" value={tone} onChange={setTone} options={TONES} />
                            <Select label="Length" value={maxLength} onChange={setMaxLength} options={LENGTHS} />
                        </div>

                        {formError && <Alert severity="error" onClose={() => setFormError('')}>{formError}</Alert>}

                        <div className="ls-hook-submit">
                            {/* Once there are hooks, "Next" is the main action, so writing more steps back */}
                            <button type="button" className={`ls-btn ${generatedHooks.length > 0 ? 'ls-btn-secondary' : 'ls-btn-primary'}`} onClick={handleGenerateHook} disabled={isLoading}>
                                {isLoading ? <span className="ls-spinner" /> : <Icon name="auto_awesome" size={16} />}
                                {generatedHooks.length > 0 ? 'Write new hooks' : 'Write hooks'}
                            </button>
                            <span className="ls-hint">Writes {HOOK_COUNT} hooks in your brand voice.</span>
                        </div>
                    </div>
                </div>

                <div className="ls-card" aria-busy={isLoading}>
                    <div className="ls-card-header">
                        <div>
                            <div className="ls-card-title">Hooks</div>
                            <div className="ls-card-sub">
                                {generatedHooks.length > 0 ? 'Pick the one to use. The next steps start from it.' : 'Your hooks will show up here.'}
                            </div>
                        </div>
                        {selectedHooks.length > 0 && (
                            <CopyButton text={selectedHooks.join('\n')} label={`Copy selected (${selectedHooks.length})`} />
                        )}
                    </div>

                    {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

                    {generatedHooks.length > 0 ? (
                        <div className="ls-results ls-stagger">
                            {generatedHooks.map((hook, index) => {
                                const isSelected = selectedHooks.includes(hook);
                                return (
                                    <div
                                        key={hook}
                                        className={`ls-result is-selectable${isSelected ? ' is-selected' : ''}`}
                                        style={{ '--i': index }}
                                        role="checkbox"
                                        aria-checked={isSelected}
                                        tabIndex={0}
                                        onClick={() => toggleHook(hook)}
                                        onKeyDown={(e) => {
                                            if (e.target === e.currentTarget && (e.key === ' ' || e.key === 'Enter')) {
                                                e.preventDefault();
                                                toggleHook(hook);
                                            }
                                        }}
                                    >
                                        <div className="ls-hook-row">
                                            <Icon className="ls-hook-check" name={isSelected ? 'check_circle' : 'radio_button_unchecked'} filled={isSelected} />
                                            <span className="ls-hook-text">{hook}</span>
                                            {/* Copying shouldn't toggle the selection */}
                                            <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                                <CopyButton text={hook} />
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="ls-placeholder">
                            {isLoading ? <span className="ls-spinner" /> : <Icon name="bolt" />}
                            {isLoading ? `Writing ${HOOK_COUNT} hooks...` : 'Fill in the brief and write your first hooks.'}
                        </div>
                    )}

                    {flow.hook && (
                        <div className="ls-cflow-using" role="status">
                            <Icon name="check_circle" size={20} filled className="ls-cflow-using-icon" />
                            <div className="ls-cflow-using-text">
                                <span className="ls-cflow-using-label">{fromPlan ? "Using your plan's hook" : 'Using'}</span>
                                <span className="ls-cflow-using-value">“{flow.hook}”</span>
                            </div>
                            {next && (
                                <button type="button" className="ls-btn ls-btn-secondary ls-cflow-using-next" onClick={goNext}>
                                    Next: {next.label}<Icon name="arrow_forward" size={18} />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default HookScreen;
